import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";
import CheckboxField from "../../components/Editing/CheckboxField";

type StateProps = {
  editedEnumeration: Api.IoBoardType;
  storedEnumeration: Api.IoBoardType;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};

const ioBoardTypeFields = nameofFactory<Api.IoBoardType>();

export type EditIoBoardTypeProps = StateProps & DispatchProps & OwnProps;

export class EditIoBoardType extends React.Component<EditIoBoardTypeProps> {
  render(): JSX.Element {
    const {
      editedEnumeration,
      storedEnumeration,
      activeUser,
      toggleModal,
      dataFieldName
    } = this.props;
    const isNew = !storedEnumeration;
    const permission = hasWriteAccess(activeUser, "Lift");
    if (storedEnumeration) {
      storedEnumeration.comment = storedEnumeration.comment ? storedEnumeration.comment : "";
      storedEnumeration.installOrder = storedEnumeration.installOrder
        ? storedEnumeration.installOrder
        : 0;
    }
    return (
      <Editor
        itemName={"Board Type"}
        originalObject={storedEnumeration}
        editedObject={editedEnumeration}
        hasWritePermission={permission}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update(dataFieldName as Api.OptionsGrud)}
        create={() => this.props.editActions.add(dataFieldName as Api.OptionsGrud)}
        remove={{ callback: toggleModal, enabledTooltip: "board type" }}
      >
        <NumberField
          id="boardTypeId"
          label="Board Type ID"
          value={editedEnumeration.boardTypeId}
          isError={!this.isFieldValid(ioBoardTypeFields("boardTypeId"))}
          errorMessage="The Type ID must be unique."
          readonly={!isNew}
          onChange={boardTypeId =>
            this.props.editActions.setEdited({ ...editedEnumeration, boardTypeId: boardTypeId })
          }
        />
        <TextField
          id="enumName"
          label="Enumeration Name"
          value={editedEnumeration.enumName}
          isError={!this.isFieldValid(ioBoardTypeFields("enumName"))}
          errorMessage={
            !this.isEmptyString(editedEnumeration.enumName)
              ? "The enumeration name must be unique."
              : ""
          }
          onChange={enumName =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() })
          }
          readonly={!permission}
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumeration.comment}
          isError={!this.isFieldValid(ioBoardTypeFields("comment"))}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedEnumeration, comment: comment.trim() })
          }
          readonly={!permission}
        />
        <CheckboxField
          id="isMultiNet"
          label="Is multinet"
          checked={editedEnumeration.isMultiNet}
          onChange={value =>
            this.props.editActions.setEdited({
              ...editedEnumeration,
              isMultiNet: value
            })
          }
          disabled={!permission}
        />
        <NumberField
          id="installOrder"
          label="Install Order"
          value={editedEnumeration.installOrder || 0}
          isError={!this.isFieldValid(ioBoardTypeFields("installOrder"))}
          onChange={installOrder =>
            this.props.editActions.setEdited({ ...editedEnumeration, installOrder: installOrder })
          }
          readonly={!permission}
        />
        {(editedEnumeration.installOrder === 0 || editedEnumeration.installOrder === null) && (
          <p className="information">
            No order defined
          </p>
        )}
      </Editor>
    );
  }

  private isFieldValid = (field: string): boolean => {
    const { editedEnumeration, storedEnumeration, pdApi } = this.props;
    if (!Number.isInteger(editedEnumeration.boardTypeId)) return false;
    const isNew = !storedEnumeration;
    switch (field) {
      case ioBoardTypeFields("boardTypeId"):
        if (this.props.storedEnumeration) {
          return true;
        }
        return _.values(pdApi[this.props.dataFieldName]).find(
          enumeration => enumeration.boardTypeId === editedEnumeration.boardTypeId
        )
          ? false
          : true;
      case ioBoardTypeFields("enumName"): {
        const { enumName } = editedEnumeration;
        if (!enumName || enumName.replace(/\s/g, "").length === 0) {
          return false;
        } else {
          return _.values(pdApi[this.props.dataFieldName]).find(
            enumeration =>
              (isNew || enumeration.boardTypeId !== editedEnumeration.boardTypeId) &&
              enumeration.enumName.trim() === editedEnumeration.enumName.trim()
          )
            ? false
            : true;
        }
      }
      default:
        return true;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedEnumeration } = this.props;
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditIoBoardType);


